/**
 * LayerManagement Module
 * 
 * Handles GeoJSON, KML, and bid layer management including:
 * - GeoJSON layer refresh and zoom handling
 * - KML layer loading and toggle functionality
 * - Layer ordering and cleanup
 */

import { MapUtilities } from './utilities';
import Logger from '../../lib/logger';

export class LayerManagement {
  constructor(controller) {
    this.controller = controller;
  }

  // Refresh GeoJSON layer to prevent display issues after zoom
  refreshGeoJSONLayer() {
    if (!this.controller.geojson || !this.controller.map.hasLayer(this.controller.geojson)) {
      return; // Layer not present, nothing to refresh
    }

    try {
      Logger.debug('Refreshing GeoJSON layer after zoom')

      // Try multiple refresh approaches for better compatibility

      // Method 1: Use Leaflet's redraw if available
      if (typeof this.controller.geojson.redraw === 'function') {
        this.controller.geojson.redraw();
        Logger.debug('Used redraw method')
      }
      // Method 2: Use bringToFront to force re-render
      else if (typeof this.controller.geojson.bringToFront === 'function') {
        this.controller.geojson.bringToFront();
        Logger.debug('Used bringToFront method')
      }
      // Method 3: Remove and re-add as fallback
      else {
        Logger.debug('Using remove/add method as fallback')
        this.controller.map.removeLayer(this.controller.geojson);
        this.controller.geojson.addTo(this.controller.map);
      }

      // Ensure proper layer ordering after refresh
      this.ensureProperLayerOrdering();

    } catch (error) {
      Logger.error('Error refreshing GeoJSON layer', error)

      // Fallback: try the remove/add approach if other methods fail
      try {
        Logger.debug('Attempting fallback refresh method')
        this.controller.map.removeLayer(this.controller.geojson);
        this.controller.geojson.addTo(this.controller.map);
        this.ensureProperLayerOrdering();
      } catch (fallbackError) {
        Logger.error('Fallback refresh also failed', fallbackError)
      }
    }
  }

  // Handle zoom end specifically for GeoJSON layer
  handleGeoZoomEnd(e) {
    Logger.debug('GeoJSON zoom end handler triggered')

    // Use a small delay to ensure the zoom operation is complete
    setTimeout(() => {
      this.refreshGeoJSONLayer();
    }, 100);
  }

  // Toggle GeoJSON layer on/off
  toggleGeo(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.controller.map.hasLayer(this.controller.geojson)) {
      // Remove geo layer and controls
      this.controller.map.removeControl(this.controller.info);
      this.controller.map.removeControl(this.controller.legend);
      this.controller.map.removeLayer(this.controller.geojson);

      // Remove zoom event listener for geojson
      this.controller.map.off('zoomend', this.handleGeoZoomEnd);
    } else {
      // Add geo layer and controls
      this.controller.info.addTo(this.controller.map);
      this.controller.legend.addTo(this.controller.map);
      this.controller.geojson.addTo(this.controller.map);

      // Add zoom event listener specifically for geojson layer
      this.controller.map.on('zoomend', this.handleGeoZoomEnd.bind(this));

      // Remove circle if present when showing geo layer
      if (this.controller.circle && this.controller.map.hasLayer(this.controller.circle)) {
        this.controller.map.removeLayer(this.controller.circle);
      }

      // Ensure proper layer ordering
      this.ensureProperLayerOrdering();
    }
  }

  // Toggle the main KML layer
  toggleKml(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.controller.mapklayer && this.controller.map.hasLayer(this.controller.mapklayer)) {
      this.controller.map.removeLayer(this.controller.mapklayer);
      Logger.debug('Toggled off KML layer');
    } else {
      if (this.controller.mapklayer) {
        this.controller.mapklayer.addTo(this.controller.map);
        Logger.debug('Toggled on existing KML layer');
      } else {
        Logger.debug('Loading KML layer for toggle');
        this.loadkml();
      }
    }
  }

  // Load KML from URL
  async loadkml() {
    try {
      // Show spinner while loading KML
      this.controller.showSpinner();

      if (!this.controller.kmlValue) {
        Logger.warn('No KML value provided')
        this.controller.hideSpinner();
        return;
      }

      // Create KML areas configuration
      const kmlAreas = L.geoJson(null, {
        onEachFeature: (feature, layer) => {
          const name = feature.properties?.name || 'Unknown Location';
          layer.bindPopup(name);
        }
      });

      // Load KML layer
      const newKmlLayer = await omnivore.kml(this.controller.kmlValue, null, kmlAreas);

      if (!newKmlLayer) {
        throw new Error('Failed to load KML layer');
      }

      // Initialize and add layer
      this.controller.mapklayer = L.featureGroup();
      this.controller.mapklayer.addLayer(newKmlLayer);
      this.controller.mapklayer.addTo(this.controller.map);

      // Setup event handlers
      newKmlLayer.on('ready', () => {
        this.controller.map.fitBounds(newKmlLayer.getBounds());
        Logger.success('KML layer loaded and fitted to bounds');
      });

      newKmlLayer.on('error', (e) => {
        Logger.error('Error loading KML', e.error);
        this.controller.showErrorMessage('Failed to load KML data. Please check the file format.');
      });

    } catch (error) {
      Logger.error('Failed to load KML', error);
      this.controller.showErrorMessage('Failed to load KML data. Please try again.');
    } finally {
      // Always hide spinner after attempting KML load
      this.controller.hideSpinner();
    }
  }

  // Add KML layer from event
  async addKml(event) {
    try {
      const { target } = event;
      const { id: tempID, name: provname, mapFile } = target.dataset;

      if (!tempID || !mapFile) {
        Logger.warn('Missing required KML data attributes', { tempID, mapFile })
        return;
      }

      // Initialize map KML layer if needed
      if (!this.controller.mapklayer) {
        this.controller.mapklayer = L.featureGroup();
        this.controller.mapklayer.addTo(this.controller.map);
      }

      // Check if layer already exists (toggle behavior)
      const existingLayer = this.findLayerByID(tempID);

      if (existingLayer) {
        // Layer exists - remove it (toggle off)
        Logger.debug(`Toggling off KML layer: ${tempID}`);
        this.removeLayerByID(tempID);

        // If no layers remain, remove the entire layer group
        if (this.controller.mapklayer.getLayers().length === 0) {
          this.controller.map.removeLayer(this.controller.mapklayer);
        }

        // Update toggle all checkbox state
        this.updateToggleAllState();
        return;
      }

      // Layer doesn't exist - add it (toggle on)
      Logger.debug(`Toggling on KML layer: ${tempID}`);

      // Create KML areas configuration
      const kmlAreas = L.geoJson(null, {
        onEachFeature: (feature, layer) => {
          const popupContent = `${provname} -> ${feature.properties?.name || 'Unknown'}`;
          layer.bindPopup(popupContent);
        }
      });

      // Load KML layer
      const newLayer = await omnivore.kml(mapFile, null, kmlAreas);

      if (!newLayer) {
        Logger.warn('Failed to load KML layer', { mapFile })
        return;
      }

      // Add new layer with ID
      newLayer.layerID = tempID;
      this.controller.mapklayer.addLayer(newLayer);

      // Ensure the layer group is on the map
      if (!this.controller.map.hasLayer(this.controller.mapklayer)) {
        this.controller.mapklayer.addTo(this.controller.map);
      }

      // Update toggle all checkbox state
      this.updateToggleAllState();

    } catch (error) {
      Logger.error('Failed to toggle KML layer', error);
      this.controller.showErrorMessage('Failed to load KML data. Please try again.');
    }
  }

  // Find layer by ID and return it
  findLayerByID(layerID) {
    if (!this.controller.mapklayer) return null;

    let foundLayer = null;
    this.controller.mapklayer.eachLayer(layer => {
      if (layer.layerID === layerID) {
        foundLayer = layer;
      }
    });

    return foundLayer;
  }

  // Check if layer with ID already exists
  checkForDuplicateLayer(layerID) {
    return this.findLayerByID(layerID) !== null;
  }

  // Remove layer by ID
  removeLayerByID(layerID) {
    if (!this.controller.mapklayer) return false;

    const layerToRemove = this.findLayerByID(layerID);
    if (layerToRemove) {
      this.controller.mapklayer.removeLayer(layerToRemove);
      Logger.debug(`Removed KML layer: ${layerID}`);
      return true;
    }

    return false;
  }

  // Toggle all KML layers on/off
  async toggleAllKml(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      const toggleCheckbox = event.target;
      const isToggleOn = toggleCheckbox.checked;

      // Find all individual KML layer checkboxes using targets instead of DOM traversal
      if (!this.controller.hasKmlCheckboxTarget) {
        Logger.warn('No individual KML checkboxes found (kmlCheckboxTarget)')
        return;
      }

      const individualCheckboxes = this.controller.kmlCheckboxTargets;

      // Set a flag to prevent updateToggleAllState from interfering
      this.controller.isTogglingAll = true;

      // Temporarily disable the toggle all checkbox to provide visual feedback
      toggleCheckbox.disabled = true;

      Logger.debug(`Starting toggle all operation: ${isToggleOn ? 'ON' : 'OFF'}`);

      if (isToggleOn) {
        // Turn on all layers that are currently off
        for (const checkbox of individualCheckboxes) {
          if (!checkbox.checked) {
            checkbox.checked = true;
            // Manually trigger the addKml method instead of dispatching events
            await this.addKmlFromCheckbox(checkbox);
          }
        }
        Logger.success('Toggled on all KML layers');
      } else {
        // Turn off all layers that are currently on
        for (const checkbox of individualCheckboxes) {
          if (checkbox.checked) {
            checkbox.checked = false;
            // Manually trigger the addKml method instead of dispatching events
            await this.addKmlFromCheckbox(checkbox);
          }
        }
        Logger.success('Toggled off all KML layers');
      }

    } catch (error) {
      Logger.error('Failed to toggle all KML layers', error);
      this.controller.showErrorMessage('Failed to toggle KML layers. Please try again.');
    } finally {
      // Always clean up the state, even if there was an error
      this.controller.isTogglingAll = false;

      // Re-enable the toggle checkbox
      const toggleCheckbox = event.target;
      if (toggleCheckbox) {
        toggleCheckbox.disabled = false;
      }

      // Update the toggle all checkbox state now that the operation is complete
      setTimeout(() => {
        this.updateToggleAllState();
      }, 100); // Small delay to ensure all individual toggles have completed

      Logger.debug('Toggle all operation completed');
    }
  }

  // Helper method to trigger KML layer toggle from checkbox
  async addKmlFromCheckbox(checkbox) {
    try {
      const { id: tempID, name: provname, mapFile } = checkbox.dataset;

      if (!tempID || !mapFile) {
        Logger.warn('Missing required KML data attributes', { tempID, mapFile })
        return;
      }

      // Initialize map KML layer if needed
      if (!this.controller.mapklayer) {
        this.controller.mapklayer = L.featureGroup();
        this.controller.mapklayer.addTo(this.controller.map);
      }

      // Check if layer already exists (toggle behavior)
      const existingLayer = this.findLayerByID(tempID);

      if (existingLayer) {
        // Layer exists - remove it (toggle off)
        Logger.debug(`Toggling off KML layer: ${tempID}`);
        this.removeLayerByID(tempID);

        // If no layers remain, remove the entire layer group
        if (this.controller.mapklayer.getLayers().length === 0) {
          this.controller.map.removeLayer(this.controller.mapklayer);
        }
        return;
      }

      // Layer doesn't exist - add it (toggle on)
      Logger.debug(`Toggling on KML layer: ${tempID}`);

      // Create KML areas configuration
      const kmlAreas = L.geoJson(null, {
        onEachFeature: (feature, layer) => {
          const popupContent = `${provname} -> ${feature.properties?.name || 'Unknown'}`;
          layer.bindPopup(popupContent);
        }
      });

      // Load KML layer
      const newLayer = await omnivore.kml(mapFile, null, kmlAreas);

      if (!newLayer) {
        Logger.warn('Failed to load KML layer', { mapFile })
        return;
      }

      // Store the ID for layer management
      newLayer.layerID = tempID;
      this.controller.mapklayer.addLayer(newLayer);

      // Add to map if not already added
      if (!this.controller.map.hasLayer(this.controller.mapklayer)) {
        this.controller.mapklayer.addTo(this.controller.map);
      }

    } catch (error) {
      Logger.error('Failed to toggle KML layer from checkbox', error);
      this.controller.showErrorMessage('Failed to load KML data. Please try again.');
    }
  }

  // Update toggle all checkbox state based on individual checkboxes
  updateToggleAllState() {
    try {
      // Skip update if currently performing a toggle all operation
      if (this.controller.isTogglingAll) {
        Logger.debug('Skipping toggle all state update during bulk operation')
        return;
      }

      // Use Stimulus target if available, fallback to querySelector
      const toggleAllCheckbox = this.controller.hasToggleAllKmlsTarget
        ? this.controller.toggleAllKmlsTarget
        : document.querySelector('input[data-action*="toggleAllKml"]');
      if (!toggleAllCheckbox) return;

      if (!this.controller.hasKmlCheckboxTarget) return;
      const individualCheckboxes = this.controller.kmlCheckboxTargets;

      const checkedCount = Array.from(individualCheckboxes).filter(cb => cb.checked).length;

      if (checkedCount === 0) {
        // None checked
        toggleAllCheckbox.checked = false;
        toggleAllCheckbox.indeterminate = false;
      } else if (checkedCount === individualCheckboxes.length) {
        // All checked
        toggleAllCheckbox.checked = true;
        toggleAllCheckbox.indeterminate = false;
      } else {
        // Some checked
        toggleAllCheckbox.checked = false;
        toggleAllCheckbox.indeterminate = true;
      }
    } catch (error) {
      Logger.error('Failed to update toggle all state', error);
    }
  }

  // Ensure proper layer ordering (circle behind, markers/popups in front)
  ensureProperLayerOrdering() {
    MapUtilities.ensureProperLayerOrdering(this.controller);
  }

  // Show/hide bids layer
  async showbids(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.controller.map.hasLayer(this.controller.bids)) {
      // Remove bids layer and event listeners
      this.controller.map.removeLayer(this.controller.bids);
      this.controller.map.off('zoomend');
      this.controller.map.off('moveend');
      this.controller.map.off('popupopen');

      // Update button state
      this.controller.updateBidsButtonState();
    } else {
      try {
        // Setup tracking for map interactions
        this.setupMapTracking();

        // Setup event listeners
        this.setupMapEventListeners();

        // Load initial bids for current view
        await this.loadBidsForCurrentView();

        // Trigger bids_crm with map area filter
        //this.triggerBidsCrmWithMapFilter();

        // Update bid providers tab (without switching to it)
        await this.controller.uiComponents.loadBidProviders();

        // Update bid list tab (without switching to it)
        await this.controller.uiComponents.loadBidList();

        // Update button state
        this.controller.updateBidsButtonState();

      } catch (error) {
        Logger.error('Failed to show bids', error);
        this.handleFetchError(error);
      }
    }
  }

  // Setup map tracking variables
  setupMapTracking() {
    this.controller.previousZoom = this.controller.map.getZoom();
    this.controller.previousBounds = this.controller.map.getBounds();
    this.controller.previousCenter = this.controller.map.getCenter();
  }

  // Setup map event listeners
  setupMapEventListeners() {
    const { MOVEEND_SUPPRESSION_DELAY } = this.controller.constructor.CONSTANTS;

    this.controller.map.on('zoomend', (e) => this.controller.handleZoomEnd(e));
    this.controller.map.on('moveend', (e) => this.controller.handleMoveEnd(e));

    // Handle popup interactions with moveend suppression
    this.controller.map.on('popupopen', () => {
      Logger.debug('Popup opened - setting immediate suppression');
      this.controller.suppressMoveend = true;

      setTimeout(() => {
        this.controller.suppressMoveend = false;
        Logger.debug('Moveend suppression cleared after popup open');
      }, MOVEEND_SUPPRESSION_DELAY);
    });
  }

  // Load bids for current map view
  async loadBidsForCurrentView() {
    try {
      this.controller.showSpinner();

      const bounds = this.controller.map.getBounds();
      const area = this.formatBoundsString(bounds);

      // Check cache first
      const cacheKey = this.createCacheKey(bounds);
      const cached = this.getCachedData(cacheKey);

      if (cached && !this.isCacheExpired(cached)) {
        Logger.debug('Using cached map data');

        // Update addresses
        this.controller.addBids(cached.data.addresses);

        // Ensure layer is on map
        if (!this.controller.map.hasLayer(this.controller.bids)) {
          this.controller.bids.addTo(this.controller.map);
          this.controller.ensureProperLayerOrdering();
          this.controller.updateBidsButtonState();
        }

        // Don't update providers here - they use the dedicated endpoint
        // which returns ALL providers (not filtered ones)

        this.controller.hideSpinner();
        return;
      }

      Logger.debug('Fetching fresh map data');

      // Build URL using base URL from data attribute
      let url = `${this.controller.dataHandling.getBaseUrl()}?l=${area}`;

      // Add provider filter if active
      const currentFilter = this.controller.getCurrentProviderFilter();
      if (currentFilter) {
        url += `&provider_id=${currentFilter.id}`;
      }

      // Add show_all parameter
      if (!this.controller.showRecentBidsOnlyValue) {
        url += '&show_all=true';
      }

      const data = await this.controller.fetchWithRetry(url);

      // Cache the data
      this.cacheData(cacheKey, data);

      // Update addresses
      this.controller.addBids(data.addresses);

      // Ensure layer is on map
      if (!this.controller.map.hasLayer(this.controller.bids)) {
        this.controller.bids.addTo(this.controller.map);
        this.controller.ensureProperLayerOrdering();
        this.controller.updateBidsButtonState();
      }

      // Don't update providers here - they use the dedicated endpoint
      // which returns ALL providers (not filtered ones)

    } finally {
      this.controller.hideSpinner();
    }
  }

  // Create cache key from bounds and current filters
  createCacheKey(bounds) {
    // Round to reasonable precision to increase cache hits (~1km)
    const precision = 0.01;
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    const roundedBounds = [
      Math.round(sw.lat / precision) * precision,
      Math.round(sw.lng / precision) * precision,
      Math.round(ne.lat / precision) * precision,
      Math.round(ne.lng / precision) * precision
    ].join(',');

    // Include filter state in cache key
    const filterKey = `recent:${this.controller.showRecentBidsOnlyValue}`;
    const currentFilter = this.controller.getCurrentProviderFilter();
    const providerKey = currentFilter ?
      `provider:${currentFilter.id}` : 'all';

    return `${roundedBounds}|${filterKey}|${providerKey}`;
  }

  // Get cached data by key
  getCachedData(key) {
    return this.controller.bidDataCache.get(key);
  }

  // Check if cached data is expired
  isCacheExpired(cached) {
    return Date.now() - cached.timestamp > this.controller.cacheExpiration;
  }

  // Cache data with timestamp
  cacheData(key, data) {
    this.controller.bidDataCache.set(key, {
      data,
      timestamp: Date.now()
    });

    // Limit cache size to prevent memory issues
    if (this.controller.bidDataCache.size > this.controller.maxCacheSize) {
      // Remove oldest entry (first in Map)
      const firstKey = this.controller.bidDataCache.keys().next().value;
      this.controller.bidDataCache.delete(firstKey);
      Logger.debug('Cache size limit reached, removed oldest entry');
    }
  }

  // Clear cache (useful when filters change)
  clearCache() {
    this.controller.bidDataCache.clear();
    Logger.debug('Bid data cache cleared');
  }

  // Format bounds as string for API
  formatBoundsString(bounds) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    return `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;
  }

  // Auto-update bid providers tab if it's currently active
  autoUpdateBidProvidersTab() {
    MapUtilities.autoUpdateBidProvidersTab(this.controller, 500);
  }

  // Handle fetch errors gracefully with improved error categorization
  handleFetchError(error) {
    const errorMessage = error.message || error.toString();

    // Categorize errors and show appropriate messages
    if (errorMessage.includes('500')) {
      this.controller.showInfoMessage(
        'Search area was too large and has been automatically adjusted. Results may be limited.',
        'info'
      );
    } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
      this.controller.showErrorMessage('Network connection error. Please check your internet connection.');
    } else if (errorMessage.includes('404')) {
      this.controller.showErrorMessage('The requested data could not be found.');
    } else if (errorMessage.includes('timeout')) {
      this.controller.showErrorMessage('Request timed out. Please try again.');
    } else {
      this.controller.showErrorMessage('Failed to load map data. Please try again.');
    }

    // Log detailed error for debugging
    Logger.error('Detailed error information', {
      message: errorMessage,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  }

  // Trigger bids_crm action with map area filter using Turbo Frame
  triggerBidsCrmWithMapFilter() {
    try {
      const bounds = this.controller.map.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      // Validate bounds
      if (sw.lat >= ne.lat || sw.lng >= ne.lng) {
        Logger.warn('Invalid map bounds for bids_crm trigger', { sw, ne })
        return;
      }

      // Format bounds as comma-separated string: "sw_lat,sw_lng,ne_lat,ne_lng"
      const boundsString = `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;

      Logger.debug('Triggering bids_crm with map area filter', { boundsString });

      // Construct the bids_crm URL with map bounds
      let bidsCrmUrl = `/bids_crm?map_bounds=${encodeURIComponent(boundsString)}`;

      // Add recent bids filter parameter if "recent bids only" is checked
      if (this.controller.showRecentBidsOnlyValue) {
        bidsCrmUrl += '&recent=1';
      }

      // Add provider filter if active
      const currentFilter = this.controller.getCurrentProviderFilter();
      if (currentFilter) {
        bidsCrmUrl += `&q%5Bprovider_id_eq%5D=${currentFilter.id}`;
        Logger.debug(`Adding provider filter to bids_crm`, {
          providerName: currentFilter.name,
          providerId: currentFilter.id
        });
      }

      Logger.debug('Final bids_crm URL', { bidsCrmUrl });

      // Use Stimulus target if available, fallback to querySelector
      const turboFrame = this.controller.hasBidsCrmFrameTarget
        ? this.controller.bidsCrmFrameTarget
        : document.querySelector('turbo-frame#bidscrm');
      if (turboFrame) {
        turboFrame.src = bidsCrmUrl;
      } else {
        Logger.warn('Turbo frame "bidscrm" not found')
      }

    } catch (error) {
      Logger.error('Failed to trigger bids_crm with map filter', error);
    }
  }

}